import { useState, useEffect, useMemo } from 'react';
import { useMatrixClient } from '../context/MatrixContext';
import { useRoomList } from './useRoomList';
import { useAvatar } from '../context/AvatarContext';
import logger from '../utils/logger';

/**
 * A hook to get detailed information for a specific contact,
 * including shared conversations.
 * @param {string} contactId - The Matrix user ID of the contact.
 * @returns {{contact: object | null, sharedConversations: object[], isLoading: boolean}}
 */
export const useContact = (contactId) => {
  const client = useMatrixClient();
  const { getUserAvatar } = useAvatar();
  const [contact, setContact] = useState(null);
  const [isLoadingContact, setIsLoadingContact] = useState(false);

  // We need the full room list to find shared conversations.
  // useRoomList provides rooms and its own loading state.
  const { roomList, isLoading: isRoomListLoading } = useRoomList();

  useEffect(() => {
    if (!client || !contactId) {
      setContact(null);
      setIsLoadingContact(false);
      return;
    }

    let isMounted = true;
    setIsLoadingContact(true);

    const loadContact = async () => {
      // Capture contactId at the start to detect if it changes mid-execution
      const targetContactId = contactId;
      
      const user = client.getUser(targetContactId);
      let displayName;
      let userId;

      if (user) {
        // User is in local store (known contact)
        displayName = user.displayName || user.userId;
        userId = user.userId;
      } else {
        // User not in local store - fetch profile from server
        // This happens for users discovered via directory search
        try {
          const profile = await client.getProfileInfo(targetContactId);
          displayName = profile.displayname || targetContactId;
          userId = targetContactId;
        } catch (error) {
          logger.error(`Failed to fetch profile for ${targetContactId}:`, error);
          // Fall back to using the user ID as the display name
          displayName = targetContactId;
          userId = targetContactId;
        }
      }

      // Check if contactId changed while we were fetching (race condition guard)
      if (!isMounted || contactId !== targetContactId) {
        return;
      }

      // Get avatar from context
      const avatar = await getUserAvatar(userId, displayName, 96);

      // Double-check contactId hasn't changed after avatar fetch
      if (!isMounted || contactId !== targetContactId) {
        return;
      }

      // Update state if component is still mounted and contactId hasn't changed
      setContact({
        id: userId,
        name: displayName,
        avatar: avatar,
      });
      setIsLoadingContact(false);
    };

    loadContact();

    return () => {
      isMounted = false;
      setIsLoadingContact(false); // Reset loading state to prevent stuck UI on cleanup
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, contactId]); // getUserAvatar excluded - stable reference from context

  const sharedConversations = useMemo(() => {
    if (!client || !contactId || isRoomListLoading) {
      return [];
    }

    const myUserId = client.getUserId();

    // If viewing self, show all conversations the user is in.
    if (contactId === myUserId) {
      return roomList;
    }

    // Find rooms that contain both the current user and the selected contact.
    // This is a simplified check. For 1-on-1 rooms, a more robust check would
    // be to see if the room has exactly two members.
    return roomList.filter(room => {
      const matrixRoom = client.getRoom(room.id);
      if (!matrixRoom) return false;

      const members = matrixRoom.getJoinedMembers().map(m => m.userId);
      return members.includes(myUserId) && members.includes(contactId);
    });
  }, [client, contactId, roomList, isRoomListLoading]);

  // The overall loading state depends on the room list being ready AND contact loading
  const isLoading = isRoomListLoading || isLoadingContact;

  return {
    contact,
    sharedConversations,
    isLoading,
  };
};

export default useContact;